import React, { useState, useEffect, useCallback } from 'react';
import {TInputParameter, TUpsertISRProjectDataModelsInput} from "../../Pages/DefineModel/model";
import TextField from 'Components/DesignSystem/TextField/TextField';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { Delete } from 'Components/DesignSystem/Iconography/Icons';
import { MenuDownIcon } from 'assests/GmsIcons';

type InputParameterRowProps = {
    parameter: TInputParameter;
    onDelete: (parameterId: string) => void;
    onUpdate: (parameterId: string, updates: Partial<TInputParameter>) => void;
    onOpenFieldSelector: (parameterId: string) => void;
    validateParameterName: (name: string, currentId: string) => string | null;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
}

// Arrow icon component matching Figma design
const MappingArrowIcon = () => (
    <svg width="37" height="16" viewBox="0 0 37 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="0" y1="8" x2="30" y2="8" stroke="#6D788C" strokeWidth="1.5"/>
        <path d="M30 8L24 4L24 12L30 8Z" fill="#6D788C"/>
    </svg>
);

const InputParameterRow = ({
                               parameter,
                               onDelete,
                               onUpdate,
                               onOpenFieldSelector,
                               validateParameterName,
                               userProjectDataModels
                           }: InputParameterRowProps) => {
    const [localName, setLocalName] = useState(parameter.paramKey);
    const [error, setError] = useState<string | null>(null);
    const [isTouched, setIsTouched] = useState(false);

    // Sync local state with prop changes
    useEffect(() => {
        setLocalName(parameter.paramKey);
    }, [parameter.paramKey]);

    const handleNameChange = useCallback((newValue: string) => {
        setLocalName(newValue);
        setIsTouched(true);

        // Validate on change
        const validationError = validateParameterName(newValue, parameter.id);
        setError(validationError);
        // Update parent state on every change (even if invalid, so parent has latest value)
        // This ensures the value is synced even if user doesn't blur before saving
        if (!validationError && newValue !== parameter.paramKey) {
            onUpdate(parameter.id, { paramKey: newValue });
        }
    }, [parameter.id, parameter.paramKey, validateParameterName, onUpdate]);
 
    const handleNameBlur = useCallback(() => {
        setIsTouched(true);
        const trimmed = localName.trim();
        if (trimmed !== localName) {
            setLocalName(trimmed);
        }
        const validationError = validateParameterName(trimmed, parameter.id);
        setError(validationError);

        if (!validationError && trimmed !== parameter.paramKey) {
            onUpdate(parameter.id, { paramKey: trimmed });
        }
    }, [localName, parameter.id, parameter.paramKey, validateParameterName, onUpdate]);

    const handleFieldClick = useCallback(() => {
        onOpenFieldSelector(parameter.id);
    }, [parameter.id, onOpenFieldSelector]);

    const handleDeleteClick = useCallback(() => {
        onDelete(parameter.id);
    }, [parameter.id, onDelete]);

    function getFieldName(subModelId: string, fieldId: string): string | null {
        const subModel = userProjectDataModels.project.dataModels.find(dm => dm.id === subModelId);
        const field = subModel?.data.fields.find(f => f.id === fieldId);
        return `${subModel?.data?.name ?? ''}.${field?.name ?? ''}`;
    }

    return (
        <tr id={`input-parameter-row-${parameter.id}`}>
            {/* Parameter Name */}
            <td className="tw-px-5 tw-py-2 tw-align-top">
                <div id={`input-parameter-name-${parameter.id}`}>
                    <TextField
                        label=""
                        value={localName}
                        onChange={handleNameChange}
                        onBlur={handleNameBlur}
                        placeholder="Enter"
                        error={!!(error && isTouched)}
                        helperText={error && isTouched ? error : ''}
                    />
                </div>
            </td>

            {/* Arrow Visual */}
            <td className="tw-px-2 tw-py-2 tw-text-center tw-align-top">
                <div className="tw-pt-2">
                    <MappingArrowIcon />
                </div>
            </td>

            {/* Mapped Field - Styled as Dropdown */}
            <td className="tw-px-4 tw-py-2 tw-align-top">
                <button data-testid="inputparameter-inputparameterrow-button-1"
                    id={`input-parameter-field-btn-${parameter.id}`}
                    onClick={handleFieldClick}
                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-edge tw-rounded-ctl tw-text-body-primary tw-text-left tw-flex tw-items-center tw-justify-between tw-transition-colors tw-bg-surface hover:tw-border-edge-strong focus:tw-outline-none focus:tw-border-accent-hover focus:tw-ring-1 focus:tw-ring-accent-hover"
                >
                    <span
                        title={parameter.fieldId ? getFieldName(parameter.subModelId, parameter.fieldId) ?? undefined : undefined}
                        className={`tw-flex-1 tw-min-w-0 tw-truncate ${parameter.fieldId ? 'tw-text-grey-900' : 'tw-text-grey-500'}`}
                    >
                        {parameter.fieldId ? getFieldName(parameter.subModelId, parameter.fieldId) : 'Add Field'}
                    </span>
                    <MenuDownIcon />
                </button>
            </td>

            {/* Actions */}
            <td className="tw-px-4 tw-py-2 tw-text-center tw-align-top">
                <div className="tw-pt-2.5">
                    <button data-testid="inputparameter-inputparameterrow-button-2"
                        id={`input-parameter-delete-btn-${parameter.id}`}
                        onClick={handleDeleteClick}
                        className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-text-ink tw-opacity-60 hover:tw-text-red-500 hover:tw-opacity-100 tw-transition-all"
                        title="Delete parameter"
                    >
                        <Delete />
                    </button>
                </div>
            </td>
        </tr>
    );
};

export default InputParameterRow;
